<template>
  <!-- Floating phone call UI — persists across route navigation -->
  <transition name="floater-slide">
    <div
      v-if="state !== 'idle' || crossTabContext"
      data-private
      class="fixed bottom-6 right-6 z-[9999]"
    >
      <!-- Cross-tab: call active in another tab -->
      <div
        v-if="crossTabContext"
        class="flex items-center gap-2 bg-neutral-900 text-white rounded-full px-4 py-2 shadow-lg cursor-pointer select-none"
        title="Click to switch to call tab"
        @click="focusCallTab"
      >
        <span class="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
        <span class="text-sm font-medium">Call active in another tab</span>
        <i class="fa-solid fa-arrow-up-right-from-square text-xs text-gray-400" />
      </div>

      <!-- Minimized pill -->
      <div
        v-else-if="minimized"
        class="flex items-center gap-3 bg-neutral-900 text-white rounded-2xl px-5 py-3 shadow-lg cursor-pointer select-none"
        @click="minimized = false"
      >
        <FontAwesomeIcon :icon="['fas', 'phone']" class="text-green-400 text-sm" />
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium">On Call</span>
          <span class="text-sm font-semibold tabular-nums">{{ formattedDuration }}</span>
        </div>
        <FontAwesomeIcon :icon="['fas', 'chevron-up']" class="text-gray-400 text-xs" />
      </div>

      <!-- Expanded card -->
      <div
        v-else
        class="bg-white rounded-xl shadow-lg border border-gray-200 w-80 overflow-hidden"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 bg-neutral-900 text-white">
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-phone text-sm" />
            <span class="text-sm font-semibold">{{ headerLabel }}</span>
          </div>
          <button
            v-if="state === 'active'"
            class="w-7 h-7 flex items-center justify-center rounded bg-white/10 hover:bg-white/25 text-white transition-colors"
            title="Minimize"
            @click="minimized = true"
          >
            <FontAwesomeIcon :icon="['fas', 'chevron-down']" class="text-xs" />
          </button>
        </div>

        <!-- Ringing inbound -->
        <div v-if="state === 'ringing-inbound'" class="p-4">
          <p class="text-sm text-gray-600 mb-1">Incoming call from</p>
          <p class="sensitive text-lg font-semibold text-neutral-900">{{ formatPhone(callerNumber) }}</p>
          <!-- Patient context during ring -->
          <p v-if="matchCount === 1" class="text-sm text-green-700 mb-4">
            <i class="fa-solid fa-user-check mr-1" />
            <a
              class="sensitive underline cursor-pointer hover:text-green-900"
              @click="goToPatient(matchedEntities[0]?.entity_id)"
            >{{ matchedEntities[0]?.entity_name }}</a>
          </p>
          <div v-else-if="matchCount > 1" class="text-sm text-yellow-700 mb-4">
            <p class="mb-1">
              <i class="fa-solid fa-users mr-1" />{{ matchCount }} patients matched
            </p>
            <ul class="ml-5 space-y-0.5">
              <li v-for="entity in matchedEntities" :key="entity.entity_id">
                <a
                  class="sensitive underline cursor-pointer hover:text-yellow-900"
                  @click="goToPatient(entity.entity_id)"
                >{{ entity.entity_name }}</a>
                <span v-if="entity.bene_mbi" class="sensitive text-yellow-600 ml-1 text-xs">{{ entity.bene_mbi }}</span>
              </li>
            </ul>
          </div>
          <div v-else-if="lookupLoading" class="mb-4">
            <div class="h-4 w-32 bg-gray-200 rounded animate-pulse" />
          </div>
          <p v-else class="text-sm text-gray-500 mb-4">
            <i class="fa-solid fa-user-xmark mr-1" />Unknown caller
          </p>
          <div class="flex gap-2">
            <button class="btn-primary-sm grow" @click="acceptCall">
              <i class="fa-solid fa-phone" />
              Accept
            </button>
            <button class="btn-danger-sm grow" @click="declineCall">
              <i class="fa-solid fa-phone-hangup" />
              Decline
            </button>
          </div>
        </div>

        <!-- Ringing outbound (connecting) -->
        <div v-else-if="state === 'ringing-outbound'" class="p-4 text-center">
          <div class="mb-3">
            <i class="fa-solid fa-phone-arrow-up-right text-2xl text-primary-500 animate-pulse" />
          </div>
          <p class="text-sm text-gray-600 mb-1">Calling</p>
          <p class="sensitive text-lg font-semibold text-neutral-900 mb-4">{{ formatPhone(callerNumber) }}</p>
          <button class="btn-danger-sm w-full" @click="endCall">
            <i class="fa-solid fa-phone-hangup" />
            Cancel
          </button>
        </div>

        <!-- Active call -->
        <div v-else-if="state === 'active'" class="p-4">
          <p class="text-sm text-gray-600 mb-1">Connected</p>
          <p v-if="callerNumber" class="sensitive text-sm font-medium text-neutral-900">{{ formatPhone(callerNumber) }}</p>
          <p class="text-lg font-semibold text-neutral-900 tabular-nums mb-3">{{ formattedDuration }}</p>

          <!-- Inbound call: the call page is where the care manager works it -->
          <div v-if="isInbound" class="mb-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
            <p v-if="entityName" class="text-sm text-green-800 font-medium mb-2">
              <i class="fa-solid fa-user-check mr-1" /><span class="sensitive" data-spoof-kind="person">{{ entityName }}</span>
            </p>
            <button
              v-if="sessionUuid"
              class="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-gray-100 transition-colors text-neutral-800"
              @click="goToInboundCall"
            >
              <i class="fa-solid fa-headset mr-1.5 w-4 text-center" />
              Open call page
            </button>
            <p v-else-if="sessionUnresolved" class="text-xs text-red-700">
              <i class="fa-solid fa-circle-exclamation mr-1" />Call page unavailable for this call
            </p>
            <p v-else class="text-xs text-gray-500">
              <i class="fa-solid fa-spinner fa-spin mr-1" />Preparing call page...
            </p>
          </div>

          <!-- Outbound call from an engagement — show navigation -->
          <div v-else-if="workId" class="mb-3 p-2 bg-green-50 rounded-lg border border-green-200">
            <p class="text-sm text-green-800 font-medium mb-2">
              <i class="fa-solid fa-user-check mr-1" /><span class="sensitive">{{ entityName }}</span>
            </p>
            <div class="space-y-1.5">
              <button
                v-if="entityId"
                class="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-green-100 transition-colors text-green-800"
                @click="goToPatient(entityId)"
              >
                <i class="fa-solid fa-user mr-1.5 w-4 text-center" />
                View Patient
              </button>
              <button
                class="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-green-100 transition-colors text-green-800"
                @click="goToEngagement"
              >
                <i class="fa-solid fa-clipboard-check mr-1.5 w-4 text-center" />
                Go to Clinical Engagement
              </button>
            </div>
          </div>

          <!-- DTMF Keypad -->
          <div v-if="showKeypad" class="mb-3">
            <div
              v-if="dtmfDisplay"
              class="text-center text-lg font-mono tracking-widest text-neutral-800 mb-2 select-all"
            >{{ dtmfDisplay }}</div>
            <div class="grid grid-cols-3 gap-1.5">
              <button
                v-for="key in dtmfKeys"
                :key="key.digit"
                class="h-11 rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors flex flex-col items-center justify-center select-none"
                @click="sendDtmf(key.digit)"
              >
                <span class="text-base font-semibold text-neutral-900 leading-none">{{ key.digit }}</span>
                <span v-if="key.sub" class="text-[9px] text-gray-400 leading-none mt-0.5 tracking-wider">{{ key.sub }}</span>
              </button>
            </div>
          </div>

          <!-- Call controls -->
          <div class="flex gap-2">
            <button
              class="grow rounded px-3 py-1.5 text-sm font-semibold transition-colors"
              :class="muted ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              @click="toggleMute"
            >
              <i :class="muted ? 'fa-solid fa-microphone-slash' : 'fa-solid fa-microphone'" />
              {{ muted ? 'Unmute' : 'Mute' }}
            </button>
            <button
              class="rounded px-3 py-1.5 text-sm font-semibold transition-colors"
              :class="showKeypad ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              title="Keypad"
              @click="showKeypad = !showKeypad"
            >
              <FontAwesomeIcon :icon="['fas', 'keyboard']" />
            </button>
            <button class="btn-danger-sm grow" @click="endCall">
              <i class="fa-solid fa-phone-hangup" />
              End
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { markRaw, toRaw } from 'vue'
import { twilioDevice } from '@/services/TwilioDeviceService'

export default {
  name: 'PhoneCallFloater',
  data() {
    return {
      state: 'idle',
      minimized: false,
      muted: false,
      callerNumber: '',
      durationSeconds: 0,
      /** @type {import('@twilio/voice-sdk').Call | null} */
      incomingCall: null,
      durationTimer: null,
      // Call context
      isInbound: false,
      sessionUuid: null,
      sessionUnresolved: false,
      navigateWhenResolved: false,
      matchCount: 0,
      matchedEntities: [],
      lookupLoading: false,
      workId: null,
      entityId: null,
      entityName: null,
      // Cross-tab
      crossTabContext: null,
      // Keypad
      showKeypad: false,
      dtmfDisplay: '',
      dtmfKeys: [
        { digit: '1', sub: '' },
        { digit: '2', sub: 'ABC' },
        { digit: '3', sub: 'DEF' },
        { digit: '4', sub: 'GHI' },
        { digit: '5', sub: 'JKL' },
        { digit: '6', sub: 'MNO' },
        { digit: '7', sub: 'PQRS' },
        { digit: '8', sub: 'TUV' },
        { digit: '9', sub: 'WXYZ' },
        { digit: '*', sub: '' },
        { digit: '0', sub: '+' },
        { digit: '#', sub: '' },
      ],
    }
  },
  computed: {
    headerLabel() {
      if (this.state === 'ringing-inbound') return 'Incoming Call'
      if (this.state === 'ringing-outbound') return 'Connecting...'
      return 'Phone Call'
    },
    formattedDuration() {
      const m = Math.floor(this.durationSeconds / 60)
      const s = this.durationSeconds % 60
      return `${m}:${String(s).padStart(2, '0')}`
    },
    onMatchingEngagement() {
      const route = this.$route
      if (route.name === 'InboundCall') {
        return !!this.sessionUuid && route.params.sessionUuid === this.sessionUuid
      }
      return (
        route.name === 'ClinicalEngagement' &&
        this.workId &&
        String(route.params.workId) === String(this.workId)
      )
    },
  },
  watch: {
    onMatchingEngagement(onPage) {
      if (onPage && this.state === 'active') {
        this.minimized = true
      }
    },
  },
  mounted() {
    twilioDevice.on('incoming', this.onIncoming)
    twilioDevice.on('incomingLookupComplete', this.onIncomingLookupComplete)
    twilioDevice.on('callConnecting', this.onConnecting)
    twilioDevice.on('callConnected', this.onConnected)
    twilioDevice.on('callRinging', this.onRinging)
    twilioDevice.on('callDisconnected', this.onDisconnected)
    twilioDevice.on('callError', this.onDisconnected)
    twilioDevice.on('muteChanged', this.onMuteChanged)
    twilioDevice.on('crossTabCall', this.onCrossTabCall)
    twilioDevice.on('inboundCallResolved', this.onInboundCallResolved)
    twilioDevice.on('inboundCallUnresolved', this.onInboundCallUnresolved)
    twilioDevice.on('engagementLinked', this.onEngagementLinked)
    this._onWindowFocus = () => this._stopFlash()
    this._onWindowBlur = () => {
      if (this.state === 'active') this._flashTitle()
    }
    window.addEventListener('focus', this._onWindowFocus)
    window.addEventListener('blur', this._onWindowBlur)
  },
  beforeUnmount() {
    twilioDevice.off('incoming', this.onIncoming)
    twilioDevice.off('incomingLookupComplete', this.onIncomingLookupComplete)
    twilioDevice.off('callConnecting', this.onConnecting)
    twilioDevice.off('callConnected', this.onConnected)
    twilioDevice.off('callRinging', this.onRinging)
    twilioDevice.off('callDisconnected', this.onDisconnected)
    twilioDevice.off('callError', this.onDisconnected)
    twilioDevice.off('muteChanged', this.onMuteChanged)
    twilioDevice.off('crossTabCall', this.onCrossTabCall)
    twilioDevice.off('inboundCallResolved', this.onInboundCallResolved)
    twilioDevice.off('inboundCallUnresolved', this.onInboundCallUnresolved)
    twilioDevice.off('engagementLinked', this.onEngagementLinked)
    window.removeEventListener('focus', this._onWindowFocus)
    window.removeEventListener('blur', this._onWindowBlur)
    this._stopTimer()
    this._stopFlash()
  },
  methods: {
    onIncoming(payload) {
      this.incomingCall = markRaw(payload.call)
      this.callerNumber = payload.fromNumber || payload.call.parameters?.From || 'Unknown'
      this.isInbound = true
      this.lookupLoading = !!payload.lookupLoading
      this.matchCount = payload.matchCount
      this.matchedEntities = payload.matchedEntities
      this.state = 'ringing-inbound'
      this.minimized = false
    },
    onIncomingLookupComplete(payload) {
      this.lookupLoading = false
      this.matchCount = payload.matchCount
      this.matchedEntities = payload.matchedEntities
    },
    onInboundCallResolved({ sessionUuid }) {
      this.sessionUuid = sessionUuid
      if (this.navigateWhenResolved) {
        this.navigateWhenResolved = false
        this.goToInboundCall()
      }
    },
    onInboundCallUnresolved() {
      this.navigateWhenResolved = false
      this.sessionUnresolved = true
    },
    onEngagementLinked(context) {
      this.workId = context.workId
      this.entityId = context.entityId
      this.entityName = context.entityName
    },
    onConnecting(call) {
      this.callerNumber = call.customParameters?.get('to_phone_number') || ''
      this.state = 'ringing-outbound'
      this.minimized = false

      const ctx = twilioDevice.currentCallContext
      if (ctx?.workId) {
        this.workId = ctx.workId
        this.entityId = ctx.entityId
        this.entityName = ctx.entityName
        this.matchCount = -1
      }
    },
    onRinging() {
      if (this.state === 'idle') {
        this.state = 'ringing-outbound'
      }
    },
    onConnected() {
      this.state = 'active'
      this.durationSeconds = 0
      this.muted = false
      this._startTimer()
      if (!document.hasFocus()) this._flashTitle()
      if (this.onMatchingEngagement) {
        this.minimized = true
      }
    },
    onCrossTabCall(context) {
      this.crossTabContext = context
    },
    focusCallTab() {
      twilioDevice.requestFocusCallTab()
    },
    _flashTitle() {
      if (this._flashTimer) return
      this._flashBaseTitle = document.title
      let on = true
      this._flashTimer = setInterval(() => {
        document.title = on ? '\u{1F4DE} PHONE CALL ACTIVE' : this._flashBaseTitle
        on = !on
      }, 800)
    },
    _stopFlash() {
      if (this._flashTimer) {
        clearInterval(this._flashTimer)
        this._flashTimer = null
        document.title = this._flashBaseTitle || document.title
        this._flashBaseTitle = null
      }
    },
    onDisconnected() {
      this._stopTimer()
      this.state = 'idle'
      this.minimized = false
      this.muted = false
      this.incomingCall = null
      this.callerNumber = ''
      this.durationSeconds = 0
      this.isInbound = false
      this.sessionUuid = null
      this.sessionUnresolved = false
      this.navigateWhenResolved = false
      this.matchCount = 0
      this.matchedEntities = []
      this.workId = null
      this.entityId = null
      this.entityName = null
      this.crossTabContext = null
      this.showKeypad = false
      this.dtmfDisplay = ''
    },
    acceptCall() {
      if (!this.incomingCall) return
      twilioDevice.acceptIncoming(toRaw(this.incomingCall))
      this.incomingCall = null
      this.navigateWhenResolved = true
    },
    async declineCall() {
      if (this.incomingCall) {
        await twilioDevice.rejectIncoming(toRaw(this.incomingCall))
        this.incomingCall = null
      }
    },
    goToPatient(entityId) {
      this.$router.push({
        name: 'CareManagement-PatientDetail',
        params: { patientId: entityId },
      })
    },
    goToInboundCall() {
      this.$router.push({
        name: 'InboundCall',
        params: { sessionUuid: this.sessionUuid },
      })
    },
    goToEngagement() {
      this.$router.push({
        name: 'ClinicalEngagement',
        params: { workId: this.workId },
      })
    },
    onMuteChanged(muted) {
      this.muted = muted
    },
    toggleMute() {
      this.muted = twilioDevice.toggleMute()
    },
    formatPhone(phone) {
      if (!phone) return 'Unknown'
      const digits = phone.replace(/\D/g, '')
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      return phone
    },
    sendDtmf(digit) {
      twilioDevice.sendDigits(digit)
      this.dtmfDisplay += digit
    },
    endCall() {
      twilioDevice.disconnect()
    },
    _startTimer() {
      this._stopTimer()
      this.durationTimer = setInterval(() => {
        this.durationSeconds++
      }, 1000)
    },
    _stopTimer() {
      if (this.durationTimer) {
        clearInterval(this.durationTimer)
        this.durationTimer = null
      }
    },
  },
}
</script>

<style scoped>
.floater-slide-enter-active,
.floater-slide-leave-active {
  transition: all 0.3s ease;
}
.floater-slide-enter-from,
.floater-slide-leave-to {
  opacity: 0;
  transform: translateY(16px);
}
</style>
